import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, FileText, Copy, Archive, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft:     "bg-slate-100 text-slate-700",
  published: "bg-emerald-100 text-emerald-800",
  archived:  "bg-amber-100 text-amber-800",
};

export default function FormsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user.role === "Admin" || user.role === "Manager";
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/forms", { params: { search: search || undefined } });
      setForms(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!newTitle.trim()) return;
    try {
      const r = await api.post("/forms", {
        title: newTitle.trim(), description: "",
        definition: { sections: [{ id: "s_main", title: "Main", description: null, fields: [] }],
                      rules: [], submit_label: "Submit",
                      success_message: "Thanks — your response has been recorded." },
      });
      setCreateOpen(false); setNewTitle("");
      navigate(`/forms/${r.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create form");
    }
  };

  const duplicate = async (fid) => {
    try {
      const r = await api.post(`/forms/${fid}/duplicate`);
      toast.success("Duplicated"); load();
      navigate(`/forms/${r.data.id}/edit`);
    } catch { toast.error("Duplicate failed"); }
  };

  const archive = async (fid) => {
    if (!window.confirm("Archive this form? Public access will be revoked immediately.")) return;
    try { await api.post(`/forms/${fid}/archive`); toast.success("Archived"); load(); }
    catch { toast.error("Archive failed"); }
  };

  const del = async (fid) => {
    if (!window.confirm("Delete this draft form? This cannot be undone.")) return;
    try { await api.delete(`/forms/${fid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const copyShareUrl = (token) => {
    const url = `${window.location.origin}/f/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public URL copied");
  };

  const filtered = forms.filter((f) => !search
    || (f.title || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="forms-list-page">
      <PageHeader
        title="Forms"
        subtitle="DYNAMIC FORMS & RESPONSES"
        action={canManage && (
          <Button onClick={() => setCreateOpen(true)}
            className="bg-[#0a2540] hover:bg-[#0a2540]/90"
            data-testid="btn-new-form">
            <Plus size={14} className="mr-1.5" /> New form
          </Button>
        )}
      />
      <div className="px-4 sm:px-8 py-6">
        <div className="mb-4">
          <Input
            placeholder="Search forms by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
            data-testid="forms-search"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={28} className="mx-auto text-slate-300 mb-2" />
              <div className="text-sm text-slate-500">No forms yet.</div>
              {canManage && (
                <Button variant="outline" size="sm" className="mt-3"
                        onClick={() => setCreateOpen(true)}>
                  <Plus size={13} className="mr-1.5" /> Create your first form
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] tracking-wide uppercase text-slate-500">Title</th>
                  <th className="px-4 py-3 text-left text-[10px] tracking-wide uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] tracking-wide uppercase text-slate-500">Submissions</th>
                  <th className="px-4 py-3 text-left text-[10px] tracking-wide uppercase text-slate-500">Public URL</th>
                  <th className="px-4 py-3 text-left text-[10px] tracking-wide uppercase text-slate-500">Updated</th>
                  <th className="px-4 py-3 sticky right-0 bg-slate-50"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`form-row-${f.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/forms/${f.id}/edit`} className="font-semibold text-[#0a2540] hover:underline">
                        {f.title}
                      </Link>
                      {f.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{f.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-sm font-semibold ${STATUS_COLORS[f.status] || ""}`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/forms/${f.id}/submissions`} className="text-[#0a2540] hover:underline font-mono">
                        {f.submission_count}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {f.share_token ? (
                        <div className="flex items-center gap-1.5">
                          <code className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded-sm truncate max-w-[140px]">
                            /f/{f.share_token.slice(0, 8)}…
                          </code>
                          <button
                            onClick={() => copyShareUrl(f.share_token)}
                            className="text-slate-400 hover:text-[#0a2540]"
                            title="Copy URL"
                            data-testid={`btn-copy-${f.id}`}
                          >
                            <Copy size={11} />
                          </button>
                          <a
                            href={`/f/${f.share_token}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-400 hover:text-[#0a2540]"
                            title="Open public form"
                          >
                            <ExternalLink size={11} />
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {f.updated_at ? new Date(f.updated_at).toLocaleString("en-IN", {
                        day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit",
                      }) : "—"}
                    </td>
                    <td className="px-4 py-3 sticky right-0 bg-white">
                      {canManage && (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => duplicate(f.id)} title="Duplicate"
                                  className="p-1.5 text-slate-400 hover:text-[#0a2540] hover:bg-slate-100 rounded-sm"
                                  data-testid={`btn-duplicate-${f.id}`}>
                            <Copy size={12} />
                          </button>
                          {f.status !== "archived" && (
                            <button onClick={() => archive(f.id)} title="Archive"
                                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-slate-100 rounded-sm"
                                    data-testid={`btn-archive-${f.id}`}>
                              <Archive size={12} />
                            </button>
                          )}
                          {f.submission_count === 0 && (
                            <button onClick={() => del(f.id)} title="Delete"
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-100 rounded-sm"
                                    data-testid={`btn-delete-${f.id}`}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-form-dialog">
          <DialogHeader><DialogTitle>New form</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Client onboarding"
                data-testid="new-form-title"
                onKeyDown={(e) => e.key === "Enter" && create()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!newTitle.trim()}
                    className="bg-[#0a2540] hover:bg-[#0a2540]/90"
                    data-testid="new-form-submit">
              Create & open builder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
